/**
 * AuthSession — single source of truth for "who is the current user?"
 *
 * Thin facade that composes four concern-specific modules:
 *   - `effects.ts` for session lifecycle (bootstrap, sign-out, refresh)
 *   - `AccountActions` for account mutations (phone, password, delete, profile, email)
 *   - `SocialFlowCoordinator` for the full social sign-in lifecycle
 *   - `PendingActionManager` for cross-redirect pending state
 *
 * Exposes a minimal public snapshot (`user | null`, `isLoaded`, `isSignedIn`)
 * via `useSyncExternalStore`. The `authSessionReducer` is the sole state
 * mutation path — all effects and actions funnel through `dispatch`.
 */

import { createAuthLogger } from '../../shared/auth-logger';
import { AuthEventBus } from '../../shared/event-bus';
import {
  NOOP_OBSERVABILITY,
  type AuthSessionDeps,
  type AuthSessionPort,
  type AuthSessionSnapshot,
  type SessionLifecycle,
  type SocialAuth,
  type AccountSettings,
  type PendingActions,
  type UserContextAuth,
} from '../../types/ports';
import type {
  AuthProviderFeatures,
  ServiceForgotPasswordResult,
  ServiceSignInResult,
  ServiceSignUpResult,
  ServiceValidationResult,
  SignUpProps,
  SocialProfile,
  SocialProvider,
  ValidateUserRequest,
} from '../../types/results';
import type { User } from '../../types/user';
import { authSessionReducer } from './reducer';
import {
  authCompleteEffect,
  bootstrapEffect,
  refreshSessionEffect,
  signOutEffect,
  userUpdatedEffect,
} from './effects';
import { AccountActions } from './account-actions';
import { PendingActionManager } from './pending-actions';
import { SocialFlowCoordinator } from '../social/coordinator';
import { navigateAfterAuth } from './return-to';
import {
  SERVER_SNAPSHOT,
  type AuthSessionInternalDeps,
  type AuthSessionState,
  type AuthSessionAction,
} from './types';

export type { AuthSessionSnapshot, AuthSessionDeps, AuthSessionPort };
export type { SessionLifecycle, SocialAuth, AccountSettings, PendingActions, UserContextAuth };
export { AuthEventBus } from '../../shared/event-bus';
export type { AuthEvent } from '../../types/events';

type StoreListener = () => void;

export class AuthSession
  implements SessionLifecycle, SocialAuth, AccountSettings, PendingActions, UserContextAuth
{
  private listeners = new Set<StoreListener>();
  private readonly deps: AuthSessionInternalDeps;
  private readonly social: SocialFlowCoordinator;
  private readonly account: AccountActions;
  private readonly eventBus = new AuthEventBus();
  private readonly pendingActions: PendingActionManager;

  private state: AuthSessionState;
  private cachedSnapshot: AuthSessionSnapshot;

  private readonly dispatchAction = (action: AuthSessionAction): void => {
    this.dispatch(action);
  };

  constructor(externalDeps: AuthSessionDeps) {
    if (externalDeps.debug) this.eventBus.enableDebug();

    this.deps = this.buildInternalDeps(externalDeps);
    this.social = this.buildSocialCoordinator();
    this.account = new AccountActions(
      () => this.deps,
      this.dispatchAction,
      () => this.state.user,
    );
    this.pendingActions = new PendingActionManager(() => this.deps.sessionStore);

    this.state = { user: externalDeps.ssrUser ?? null, loaded: false };
    this.cachedSnapshot = this.buildSnapshot();

    bootstrapEffect(externalDeps.ssrUser, this.dispatchAction, this.deps);
  }

  // ─── @internal (used by useSignIn, useSignUp, useAuthEvent) ────────────────

  get internals() {
    return {
      service: this.deps.service,
      observability: this.deps.observability,
      logger: this.deps.logger,
      eventBus: this.eventBus,
    } as const;
  }

  // ─── SessionLifecycle ──────────────────────────────────────────────────────

  get supportedFeatures(): AuthProviderFeatures {
    return this.deps.service.supportedFeatures;
  }

  subscribe = (listener: StoreListener): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  getSnapshot = (): AuthSessionSnapshot => this.cachedSnapshot;

  getServerSnapshot = (): AuthSessionSnapshot => SERVER_SNAPSHOT;

  destroy(): void {
    this.social?.destroy();
  }

  completeAuth(user: User): void {
    authCompleteEffect(user, this.dispatchAction, this.deps);
  }

  async signOut(redirectUrl?: string): Promise<void> {
    await signOutEffect(redirectUrl, this.dispatchAction, this.deps);
  }

  setUser(user: User): void {
    userUpdatedEffect(user, this.dispatchAction, this.deps);
  }

  async refreshSession(): Promise<void> {
    await refreshSessionEffect(this.dispatchAction, this.deps);
  }

  async getSignInProvider(): Promise<string | null> {
    return this.deps.service.getSignInProvider();
  }

  getLogoutUrl(redirectUri: string): string | null {
    return this.deps.service.getLogoutUrl(redirectUri);
  }

  // ─── SocialAuth ────────────────────────────────────────────────────────────

  signInWithSocial(provider: SocialProvider): void {
    if (this.supportedFeatures.socialSignInFlow === 'idp_redirect') {
      this.social.beginViaLogout(provider);
    }
  }

  /** @deprecated Legacy client-app path only. Posts an SDK-obtained token to Rails. */
  async legacySocialSignIn(profile: SocialProfile): Promise<void> {
    const result = await this.deps.service.legacySocialSignIn(profile);
    if (result.user) {
      this.completeAuth(result.user);
      if (result.needsPhone) {
        this.eventBus.emit({ type: 'needs_phone_post_auth', provider: profile.provider });
      }
    }
  }

  hasPendingAfterLogout(): boolean {
    return this.social.hasPendingAfterLogout();
  }

  resumeAfterLogout(): void {
    this.social.resumeAfterLogout();
  }

  failSocialAuth(error: string): void {
    this.social.failSocialAuth(error);
  }

  /** @internal Hub bridge — called by infrastructure, not by UI code. */
  setSocialRedirectState(encodedState: string): void {
    this.social.setSocialRedirectState(encodedState);
  }

  /** @internal */
  cognitoSocialSignInComplete(): void {
    this.social.cognitoSocialSignInComplete();
  }

  /** @internal */
  legacySdkSignInComplete(profile: SocialProfile): void {
    this.social.legacySdkSignInComplete(profile);
  }

  // ─── AccountSettings ───────────────────────────────────────────────────────

  async submitPhone(phone: string, profile?: { firstName: string; lastName: string }): Promise<{ ok: true } | { ok: false; error: string }> {
    return this.account.submitPhone(phone, profile);
  }

  async deleteAccount(): Promise<{ ok: true } | { ok: false; error: string }> {
    return this.account.deleteAccount();
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    return this.account.changePassword(currentPassword, newPassword);
  }

  async updateProfile(params: User): Promise<{ ok: true } | { ok: false; error: string }> {
    return this.account.updateProfile(params);
  }

  async updateEmail(
    currentUser: User,
    newEmail: string,
    retypeEmail: string,
    password?: string,
    passwordConfirmation?: string,
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    return this.account.updateEmail(currentUser, newEmail, retypeEmail, password, passwordConfirmation);
  }

  async confirmEmailUpdate(code: string): Promise<{ ok: true } | { ok: false; error: string }> {
    return this.account.confirmEmailUpdate(code);
  }

  // ─── PendingActions ────────────────────────────────────────────────────────

  consumePendingAction() { return this.pendingActions.consume(); }
  peekPendingAction() { return this.pendingActions.peek(); }
  clearPendingAction() { this.pendingActions.clear(); }

  getSocialProfile(): { firstName: string; lastName: string } | null {
    return this.deps.sessionStore.getSocialProfile();
  }

  // ─── UserContextAuth ───────────────────────────────────────────────────────

  async signIn(email: string, password: string): Promise<ServiceSignInResult> {
    return this.deps.service.signIn(email, password);
  }

  async signUp(props: SignUpProps): Promise<ServiceSignUpResult> {
    return this.deps.service.signUp(props);
  }

  async confirmSignUp(email: string, code: string): Promise<ServiceSignUpResult> {
    return this.deps.service.confirmSignUp(email, code);
  }

  async resendSignUpCode(email: string): Promise<void> {
    return this.deps.service.resendSignUpCode(email);
  }

  async validateUser(request: ValidateUserRequest): Promise<ServiceValidationResult> {
    return this.deps.service.validateUser(request);
  }

  async forgotPassword(email: string, theme: string): Promise<ServiceForgotPasswordResult> {
    return this.deps.service.forgotPassword(email, theme);
  }

  async confirmForgotPassword(
    email: string,
    code: string,
    password: string,
    theme: string,
  ): Promise<ServiceForgotPasswordResult> {
    return this.deps.service.confirmForgotPassword(email, code, password, theme);
  }

  private dispatch(action: AuthSessionAction): void {
    this.state = authSessionReducer(this.state, action);
    const next = this.buildSnapshot();
    const prev = this.cachedSnapshot;

    this.emitSessionEvent(action);

    if (
      next.user !== prev.user ||
      next.isLoaded !== prev.isLoaded ||
      next.isSignedIn !== prev.isSignedIn
    ) {
      this.cachedSnapshot = next;
      this.listeners.forEach(fn => fn());
    }
  }

  private emitSessionEvent(action: AuthSessionAction): void {
    if (action.type === 'AUTH_COMPLETE' && action.user) {
      this.eventBus.emit({ type: 'auth_complete', user: action.user });
    }
  }

  private buildSnapshot(): AuthSessionSnapshot {
    return {
      user: this.state.user,
      isLoaded: this.state.loaded,
      isSignedIn: this.state.user !== null,
    };
  }

  private buildInternalDeps(deps: AuthSessionDeps): AuthSessionInternalDeps {
    return {
      service: deps.authService,
      persistence: deps.persistence,
      sessionStore: deps.sessionStore,
      logger: createAuthLogger(deps.debug ?? false),
      observability: deps.observability ?? NOOP_OBSERVABILITY,
      eventBus: this.eventBus,
      replaceLocation: deps.restoreReturnTo
        ? url => { if (typeof window !== 'undefined') deps.restoreReturnTo!(url); }
        : url => { if (typeof window !== 'undefined') window.location.assign(url); },
    };
  }

  private buildSocialCoordinator(): SocialFlowCoordinator {
    return new SocialFlowCoordinator(
      {
        service: this.deps.service,
        sessionStore: this.deps.sessionStore,
        observability: this.deps.observability,
        eventBus: this.eventBus,
        logger: this.deps.logger,
        replaceLocation: this.deps.replaceLocation,
      },
      {
        onAuthComplete: user => this.completeAuth(user),
        onNeedsPhone: returnTo => {
          this.deps.sessionStore.dispatch({ type: 'SET_NEEDS_PHONE' });
          navigateAfterAuth(returnTo, this.deps.replaceLocation);
        },
        onError: (code, message) => {
          this.deps.sessionStore.dispatch({ type: 'SET_PENDING_ERROR', error: { code, message } });
        },
      },
    );
  }
}

export function createAuthSession(deps: AuthSessionDeps): AuthSession {
  return new AuthSession(deps);
}
